import { useEffect, useState, useCallback } from 'react'
import { useToast } from '@/hooks/use-toast'
import { ToastAction } from '@/components/ui/toast'
import { getTimeUntilExpiry } from '@/lib/jwt'
import { apiClient } from '@/lib/api'

const WARNING_TIME = 5 * 60 * 1000 // 5 minutes before expiry

/**
 * Hook that monitors JWT token expiration and shows a warning toast
 * before the token expires, allowing users to extend their session.
 */
export function useSessionWarning() {
  const { toast } = useToast()
  const [warningShown, setWarningShown] = useState(false)

  const extendSession = useCallback(async () => {
    const success = await apiClient.refreshAccessToken()
    if (success) {
      setWarningShown(false)
      toast({
        title: 'Session Extended',
        description: 'Your session has been extended.',
      })
    } else {
      toast({
        title: 'Session Expired',
        description: 'Please log in again.',
        variant: 'destructive',
      })
      window.location.href = '/login?error=session_expired'
    }
  }, [toast])

  useEffect(() => {
    const checkExpiry = () => {
      const token = localStorage.getItem('accessToken')
      if (!token) return

      const timeUntilExpiry = getTimeUntilExpiry(token)
      if (timeUntilExpiry === null) return

      if (timeUntilExpiry <= WARNING_TIME && timeUntilExpiry > 0 && !warningShown) {
        setWarningShown(true)
        const minutes = Math.ceil(timeUntilExpiry / 60000)

        toast({
          title: 'Session Expiring Soon',
          description: `Your session will expire in ${minutes} minute${minutes === 1 ? '' : 's'}.`,
          action: (
            <ToastAction altText="Stay logged in" onClick={extendSession}>
              Stay Logged In
            </ToastAction>
          ),
          duration: timeUntilExpiry, // Keep visible until expiry
        })
      }
    }

    // Check immediately and every 30 seconds
    checkExpiry()
    const interval = setInterval(checkExpiry, 30000)

    return () => clearInterval(interval)
  }, [toast, warningShown, extendSession])

  return { extendSession, warningShown }
}
